import { printHtml } from '../../utils/printUtils';
import PrintButton from '../shared/PrintButton';
import React, { useState, useEffect, useCallback } from 'react';
import { Trophy, Ticket, Gift, Search, Plus, Edit2, Trash2,

         Play, CheckCircle, AlertCircle, RefreshCw, Printer,
         ChevronDown, Star, Crown, Award, Zap } from 'lucide-react';

// ── DROP 39B-4: Auth token helper ────────────────────────────────────────────
function re_getToken(): string {
  return localStorage.getItem('erue_auth_token') || sessionStorage.getItem('erue_auth_token') || '';
}
function re_authHeaders(extra?: Record<string,string>): Record<string,string> {
  const t = re_getToken();
  return {
    'Content-Type': 'application/json',
    ...(t ? { 'Authorization': 'Bearer ' + t } : {}),
    ...(extra || {}),
  };
}


// ── DROP 22: Raffle Operational ─────────────────────────────────

interface RafflePrize {
  id: string; name: string; tier: string; description: string;
  value: string; imageUrl: string; won: boolean;
  winnerTicket: string | null; winnerName: string | null;
  drawnAt: string | null; sortOrder: number; isActive: boolean;
}
interface RaffleTicket {
  id: string; ticketNumber: string; holderName: string;
  holderPhone: string; holderEmail: string; paymentReference: string;
  paymentStatus: string; purchasedAt: string; drawnForPrize: string | null;
}
interface RaffleResult {
  id: string; prizeId: string; prizeName: string; prizeTier: string;
  prizeValue: string; winnerTicket: string; winnerName: string;
  winnerPhone: string; drawnAt: string; drawnBy: string;
}
interface RaffleConfig { ticketPrice: number; currency: string; isOpen: boolean; }

const TIER_STYLE: Record<string, { bg: string; border: string; label: string; icon: React.ReactNode }> = {
  GRAND:       { bg: 'rgba(201,168,76,0.12)', border: 'rgba(201,168,76,0.6)',  label: 'Grand Prize',       icon: <Crown size={18} style={{ color:'#C9A84C' }} /> },
  MEDIUM:      { bg: 'rgba(192,132,252,0.1)', border: 'rgba(192,132,252,0.4)', label: 'Medium Prize',      icon: <Star  size={18} style={{ color:'#C084FC' }} /> },
  MINI:        { bg: 'rgba(52,211,153,0.1)',  border: 'rgba(52,211,153,0.4)',  label: 'Mini Prize',        icon: <Award size={18} style={{ color:'#34D399' }} /> },
  CONSOLATION: { bg: 'rgba(96,165,250,0.1)',  border: 'rgba(96,165,250,0.4)',  label: 'Consolation Prize', icon: <Gift  size={18} style={{ color:'#60A5FA' }} /> },
};

const S = {
  page:   { background: '#070D0A', minHeight: '100vh', color: '#EDE8DC', fontFamily: 'Plus Jakarta Sans, sans-serif' },
  card:   { background: '#0E1812', border: '1px solid #1C2E22', borderRadius: 12, padding: 20 },
  input:  { background: '#0A1410', border: '1px solid #1C2E22', borderRadius: 8, padding: '10px 14px',
            color: '#EDE8DC', fontSize: 14, width: '100%', outline: 'none', fontFamily: 'Plus Jakarta Sans, sans-serif' },
  btn:    { padding: '10px 20px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13,
            fontWeight: 600, fontFamily: 'Plus Jakarta Sans, sans-serif', transition: 'all 0.2s' },
  gold:   { background: 'linear-gradient(135deg,#C9A84C,#E2C97E)', color: '#070D0A' },
  green:  { background: '#0F5132', color: '#E2C97E', border: '1px solid #1C2E22' },
  danger: { background: 'rgba(239,68,68,0.15)', color: '#F87171', border: '1px solid rgba(239,68,68,0.3)' },
  ghost:  { background: 'transparent', color: '#EDE8DCCC', border: '1px solid #1C2E22' },
  label:  { fontSize: 11, color: '#C9A84C', letterSpacing: 3, textTransform: 'uppercase' as const,
            fontFamily: 'Plus Jakarta Sans, sans-serif', marginBottom: 6, display: 'block' },
  eyebrow:{ fontSize: 11, color: '#C9A84C', letterSpacing: 4, textTransform: 'uppercase' as const, marginBottom: 10 },
  h2:     { fontFamily: 'Cinzel, serif', fontSize: 'clamp(1.3rem,3vw,1.8rem)', color: '#EDE8DC', marginBottom: 8 },
};

function fmt(n: number) {
  return new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', maximumFractionDigits: 0 }).format(n);
}

function printTicket(ticket: RaffleTicket, cfg: RaffleConfig) {
  const w = window.open('', '_blank', 'width=420,height=580');
  if (!w) return;
  w.document.write(`<!DOCTYPE html><html><head><style>
    body{font-family:'Courier New',monospace;text-align:center;padding:24px;background:#fff;color:#111}
    .box{border:2px dashed #333;padding:24px;border-radius:10px;max-width:340px;margin:0 auto}
    .logo{font-size:22px;font-weight:900;letter-spacing:2px;color:#0F5132;margin-bottom:4px}
    .sub{font-size:11px;color:#555;margin-bottom:16px;letter-spacing:1px}
    .num{font-size:32px;font-weight:900;background:#f5f0e0;border:2px solid #C9A84C;
         border-radius:8px;padding:12px;margin:16px 0;letter-spacing:3px;color:#0F5132}
    .holder{font-size:14px;margin:6px 0}
    .detail{font-size:11px;color:#777;margin:3px 0}
    .footer{font-size:10px;color:#999;margin-top:18px;border-top:1px dashed #ccc;padding-top:10px}
  </style></head><body onload="window.print();setTimeout(()=>window.close(),500)">
    <div class="box">
      <div class="logo">ERUE FESTIVAL 2026</div>
      <div class="sub">OFFICIAL RAFFLE TICKET</div>
      <div class="num">${ticket.ticketNumber}</div>
      <div class="holder"><strong>${ticket.holderName}</strong></div>
      <div class="detail">📞 ${ticket.holderPhone}</div>
      <div class="detail">🎟️ Price: ${fmt(cfg.ticketPrice)} per ticket</div>
      <div class="detail">📅 Purchased: ${new Date(ticket.purchasedAt).toLocaleDateString('en-NG')}</div>
      <div class="footer">
        Grand Finale: Wednesday 30 December 2026<br>
        Palace of the Ogieneni of Uzairue Kingdom, Jattu-Uzairue<br>
        "One People, One Kingdom, One Destiny"
      </div>
    </div>
  </body></html>`);
  w.document.close();
}

// ── SUBCOMPONENTS ────────────────────────────────────────────────

function PrizeCard({ prize, onBuy }: { key?: React.Key; prize: RafflePrize; onBuy: () => void }) {
  const style = TIER_STYLE[prize.tier] || TIER_STYLE.CONSOLATION;
  return (
    <div style={{ ...S.card, background: style.bg, border: `1px solid ${style.border}`,
                  opacity: prize.won ? 0.75 : 1, position: 'relative', overflow: 'hidden' }}>
      {prize.won && (
        <div style={{ position: 'absolute', top: 10, right: 10, background: 'rgba(201,168,76,0.9)',
                      color: '#070D0A', fontSize: 10, fontWeight: 700, padding: '3px 10px',
                      borderRadius: 20, letterSpacing: 1 }}>
          🏆 WON
        </div>
      )}
      {prize.imageUrl && (
        <div style={{ height: 140, borderRadius: 8, overflow: 'hidden', marginBottom: 14,
                      background: '#0A1410', border: '1px solid #1C2E22' }}>
          <img src={prize.imageUrl} alt={prize.name}
               style={{ width: '100%', height: '100%', objectFit: 'cover' }}
               onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        {style.icon}
        <span style={{ fontSize: 10, color: '#EDE8DC80', letterSpacing: 2,
                       textTransform: 'uppercase', fontWeight: 600 }}>{style.label}</span>
      </div>
      <h3 style={{ fontFamily: 'Cinzel, serif', fontSize: 16, color: '#EDE8DC', marginBottom: 6 }}>
        {prize.name}
      </h3>
      {prize.value && (
        <p style={{ fontSize: 15, color: '#C9A84C', fontWeight: 700, marginBottom: 6 }}>
          {prize.value}
        </p>
      )}
      {prize.description && (
        <p style={{ fontSize: 13, color: '#EDE8DC99', lineHeight: 1.6, marginBottom: 14 }}>
          {prize.description}
        </p>
      )}
      {prize.won && prize.winnerName ? (
        <div style={{ background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.3)',
                      borderRadius: 8, padding: '10px 14px', fontSize: 13 }}>
          <span style={{ color: '#C9A84C' }}>🏆 Winner: </span>
          <span style={{ color: '#EDE8DC' }}>{prize.winnerName}</span>
          <span style={{ color: '#EDE8DC60', fontSize: 11, display: 'block', marginTop: 2 }}>
            Ticket: {prize.winnerTicket}
          </span>
        </div>
      ) : (
        <button onClick={onBuy} style={{ ...S.btn, ...S.gold, width: '100%' }}>
          🎟️ Buy Ticket Now
        </button>
      )}
    </div>
  );
}

function BuyTicketModal({ config, paystackKey, onClose }: {
  config: RaffleConfig; paystackKey: string; onClose: () => void;
}) {
  const [form, setForm] = useState({ name: '', phone: '', email: '', qty: 1 });
  const [step, setStep] = useState<'form'|'processing'|'success'|'error'>('form');
  const [tickets, setTickets] = useState<RaffleTicket[]>([]);
  const [errMsg, setErrMsg] = useState('');
  const total = form.qty * config.ticketPrice;

  const handlePay = async () => {
    if (!form.name.trim() || !form.phone.trim() || !form.email.trim())
      return setErrMsg('Please fill all fields');
    if (!/\S+@\S+\.\S+/.test(form.email))
      return setErrMsg('Please enter a valid email address');
    setErrMsg('');
    setStep('processing');
    try {
      const r = await fetch('/api/raffle/initiate-payment', {
        method: 'POST',
        headers: re_authHeaders(),
        body: JSON.stringify({
          holderName: form.name, holderPhone: form.phone,
          holderEmail: form.email, quantity: form.qty
        })
      });
      const d = await r.json();
      if (!d.success) { setErrMsg(d.error || 'Payment initiation failed'); setStep('error'); return; }
      // Open Paystack in new window
      const payWin = window.open(d.authorization_url, '_blank', 'width=520,height=700');
      // Poll for verification every 3 seconds
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        if (attempts > 60 || !payWin || payWin.closed) {
          // Try to verify anyway
          clearInterval(poll);
          try {
            const vr = await fetch('/api/raffle/verify-payment', {
              method: 'POST',
              headers: re_authHeaders(),
              body: JSON.stringify({ reference: d.reference })
            });
            const vd = await vr.json();
            if (vd.success) { setTickets(vd.tickets); setStep('success'); }
            else { setErrMsg('Payment window closed. If you paid, contact support with reference: ' + d.reference); setStep('error'); }
          } catch { setStep('error'); setErrMsg('Verification failed. Reference: ' + d.reference); }
          return;
        }
        try {
          const vr = await fetch('/api/raffle/verify-payment', {
            method: 'POST',
            headers: re_authHeaders(),
            body: JSON.stringify({ reference: d.reference })
          });
          const vd = await vr.json();
          if (vd.success) { clearInterval(poll); if (payWin && !payWin.closed) payWin.close(); setTickets(vd.tickets); setStep('success'); }
        } catch { /* keep polling */ }
      }, 3000);
    } catch { setErrMsg('Network error. Please try again.'); setStep('error'); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1000,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ ...S.card, maxWidth: 480, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ fontFamily: 'Cinzel, serif', fontSize: 18, color: '#EDE8DC' }}>
            🎟️ Buy Raffle Tickets
          </h3>
          <button onClick={onClose} style={{ ...S.btn, ...S.ghost, padding: '6px 12px' }}>✕</button>
        </div>

        {step === 'form' && (
          <>
            <div style={{ background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.2)',
                          borderRadius: 8, padding: '12px 16px', marginBottom: 20, fontSize: 13 }}>
              <span style={{ color: '#C9A84C' }}>Ticket Price: </span>
              <span style={{ color: '#EDE8DC', fontWeight: 700 }}>{fmt(config.ticketPrice)} each</span>
            </div>
            {['name','phone','email'].map(field => (
              <div key={field} style={{ marginBottom: 14 }}>
                <label style={S.label}>
                  {field === 'name' ? 'Full Name' : field === 'phone' ? 'Phone Number' : 'Email Address'}
                </label>
                <input
                  style={S.input}
                  type={field === 'email' ? 'email' : field === 'phone' ? 'tel' : 'text'}
                  placeholder={field === 'phone' ? '08012345678' : field === 'email' ? 'you@example.com' : 'Your full name'}
                  value={(form as any)[field]}
                  onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
                />
              </div>
            ))}
            <div style={{ marginBottom: 20 }}>
              <label style={S.label}>Number of Tickets</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <button onClick={() => setForm(f => ({ ...f, qty: Math.max(1, f.qty - 1) }))}
                        style={{ ...S.btn, ...S.ghost, padding: '8px 16px', fontSize: 18 }}>−</button>
                <span style={{ fontSize: 24, fontWeight: 700, color: '#C9A84C', minWidth: 40, textAlign: 'center' }}>
                  {form.qty}
                </span>
                <button onClick={() => setForm(f => ({ ...f, qty: Math.min(20, f.qty + 1) }))}
                        style={{ ...S.btn, ...S.ghost, padding: '8px 16px', fontSize: 18 }}>+</button>
                <span style={{ fontSize: 13, color: '#EDE8DC80' }}>max 20</span>
              </div>
            </div>
            {errMsg && (
              <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
                            borderRadius: 8, padding: '10px 14px', color: '#F87171', fontSize: 13, marginBottom: 14 }}>
                ⚠️ {errMsg}
              </div>
            )}
            <div style={{ background: '#0A1410', borderRadius: 8, padding: '14px 16px',
                          marginBottom: 20, display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#EDE8DC80', fontSize: 14 }}>Total ({form.qty} ticket{form.qty > 1 ? 's' : ''})</span>
              <span style={{ color: '#C9A84C', fontWeight: 700, fontSize: 18 }}>{fmt(total)}</span>
            </div>
            <button onClick={handlePay} style={{ ...S.btn, ...S.gold, width: '100%', fontSize: 15, padding: '14px' }}>
              💳 Pay {fmt(total)} via Paystack
            </button>
            <p style={{ textAlign: 'center', fontSize: 11, color: '#EDE8DC40', marginTop: 12 }}>
              Secured by Paystack · Your payment opens in a new window
            </p>
          </>
        )}

        {step === 'processing' && (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <div style={{ fontSize: 48, marginBottom: 20 }}>⏳</div>
            <p style={{ color: '#C9A84C', fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
              Payment Window Opened
            </p>
            <p style={{ color: '#EDE8DC80', fontSize: 13, lineHeight: 1.6 }}>
              Complete your payment in the Paystack window.<br />
              Your tickets will be assigned automatically after payment.
            </p>
            <div style={{ marginTop: 24 }}>
              <RefreshCw size={20} style={{ color: '#C9A84C', animation: 'spin 2s linear infinite' }} />
              <p style={{ color: '#EDE8DC60', fontSize: 12, marginTop: 8 }}>Waiting for payment confirmation...</p>
            </div>
          </div>
        )}

        {step === 'success' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 64, marginBottom: 16 }}>🎉</div>
            <h3 style={{ fontFamily: 'Cinzel, serif', color: '#C9A84C', fontSize: 20, marginBottom: 8 }}>
              Payment Successful!
            </h3>
            <p style={{ color: '#EDE8DC80', fontSize: 13, marginBottom: 24 }}>
              Your {tickets.length} ticket{tickets.length > 1 ? 's have' : ' has'} been assigned.
            </p>
            {tickets.map(t => (
              <div key={t.id} style={{ ...S.card, marginBottom: 10, display: 'flex',
                                       justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <p style={{ fontFamily: 'Cinzel, serif', fontSize: 18, color: '#C9A84C', letterSpacing: 3 }}>
                    {t.ticketNumber}
                  </p>
                  <p style={{ fontSize: 12, color: '#EDE8DC60' }}>{t.holderName}</p>
                </div>
                <button onClick={() => printTicket(t, config)}
                        style={{ ...S.btn, ...S.ghost, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Printer size={14} /> Print
                </button>
              </div>
            ))}
            <div style={{ background: 'rgba(37,211,102,0.08)', border: '1px solid rgba(37,211,102,0.25)',
                          borderRadius: 8, padding: '12px 16px', marginTop: 16, fontSize: 13 }}>
              <p style={{ color: '#25D366', fontWeight: 600 }}>📱 Keep your ticket numbers safe!</p>
              <p style={{ color: '#EDE8DC80', marginTop: 4 }}>
                Grand Finale Draw: Wednesday 30 December 2026<br />
                Palace of the Ogieneni, Jattu-Uzairue
              </p>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <PrintButton
                label="Print All Tickets"
                size="md"
                variant="ghost"
                fullWidth={true}
                onClick={() => {
                  tickets.forEach(t => printTicket(t, config));
                }}
              />
            </div>
            <button onClick={onClose} style={{ ...S.btn, ...S.gold, width: '100%', marginTop: 10 }}>
              Done
            </button>
          </div>
        )}

        {step === 'error' && (
          <div style={{ textAlign: 'center', padding: 20 }}>
            <AlertCircle size={48} style={{ color: '#F87171', marginBottom: 16 }} />
            <p style={{ color: '#F87171', fontWeight: 600, marginBottom: 8 }}>Payment Issue</p>
            <p style={{ color: '#EDE8DC80', fontSize: 13, lineHeight: 1.6, marginBottom: 20 }}>{errMsg}</p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setStep('form')} style={{ ...S.btn, ...S.ghost, flex: 1 }}>Try Again</button>
              <button onClick={onClose} style={{ ...S.btn, ...S.gold, flex: 1 }}>Close</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function DrawPanel({ prizes, onDraw, summary }: {
  prizes: RafflePrize[];
  onDraw: (prizeId: string, redraw?: boolean) => Promise<RaffleResult | null>;
  summary: any;
}) {
  const [selectedPrize, setSelectedPrize] = useState('');
  const [drawing, setDrawing] = useState(false);
  const [animStep, setAnimStep] = useState(0);
  const [lastResult, setLastResult] = useState<RaffleResult | null>(null);
  const [animTickets] = useState(['ERUE-????-?????','ERUE-2026-XXXXX','ERUE-2026-????']);

  const available = prizes.filter(p => p.isActive);
  const undrawn = available.filter(p => !p.won);
  const drawn = available.filter(p => p.won);

  const executeDraw = async (redraw = false) => {
    if (!selectedPrize) return alert('Please select a prize first');
    setDrawing(true);
    setLastResult(null);
    // Animate drum roll
    for (let i = 0; i < 12; i++) {
      setAnimStep(i);
      await new Promise(r => setTimeout(r, 150 + i * 30));
    }
    const result = await onDraw(selectedPrize, redraw);
    setDrawing(false);
    setAnimStep(0);
    if (result) { setLastResult(result); setSelectedPrize(''); }
  };

  return (
    <div>
      {/* Summary bar */}
      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(140px,1fr))', gap: 10, marginBottom: 24 }}>
          {[
            { label: 'Tickets Sold', value: summary.totalTicketsSold, color: '#C9A84C' },
            { label: 'Revenue',      value: fmt(summary.totalRevenue), color: '#34D399' },
            { label: 'Prizes Left',  value: summary.prizesRemaining, color: '#60A5FA' },
            { label: 'Eligible',     value: summary.eligibleTickets,  color: '#C084FC' },
          ].map(item => (
            <div key={item.label} style={{ ...S.card, textAlign: 'center' }}>
              <p style={{ fontSize: 11, color: '#EDE8DC60', marginBottom: 4 }}>{item.label}</p>
              <p style={{ fontSize: 18, fontWeight: 700, color: item.color }}>{item.value}</p>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))', gap: 20 }}>
        {/* Draw control */}
        <div style={S.card}>
          <h4 style={{ fontFamily: 'Cinzel, serif', color: '#C9A84C', marginBottom: 16, fontSize: 16 }}>
            🎛️ Draw Control Panel
          </h4>
          <label style={S.label}>Select Prize</label>
          <select value={selectedPrize} onChange={e => setSelectedPrize(e.target.value)}
                  style={{ ...S.input, marginBottom: 16, cursor: 'pointer' }}>
            <option value=''>-- Choose a prize --</option>
            {undrawn.map(p => (
              <option key={p.id} value={p.id}>
                {TIER_STYLE[p.tier]?.label || p.tier}: {p.name} {p.value ? `(${p.value})` : ''}
              </option>
            ))}
            {drawn.length > 0 && (
              <optgroup label='── Already Drawn (Redraw) ──'>
                {drawn.map(p => (
                  <option key={p.id} value={p.id}>🔁 {p.name} — WON by {p.winnerName}</option>
                ))}
              </optgroup>
            )}
          </select>

          {/* Drum roll animation */}
          {drawing && (
            <div style={{ background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.3)',
                          borderRadius: 8, padding: 20, textAlign: 'center', marginBottom: 16 }}>
              <p style={{ fontSize: 11, color: '#C9A84C', letterSpacing: 3, marginBottom: 12 }}>
                DRAWING...
              </p>
              <p style={{ fontFamily: 'Cinzel, serif', fontSize: 22, color: '#EDE8DC',
                          letterSpacing: 4, animation: 'pulse 0.3s infinite' }}>
                {animTickets[animStep % animTickets.length]}
              </p>
              <p style={{ fontSize: 12, color: '#EDE8DC40', marginTop: 8 }}>🎲 Selecting winner...</p>
            </div>
          )}

          {lastResult && (
            <div style={{ background: 'rgba(201,168,76,0.12)', border: '2px solid rgba(201,168,76,0.5)',
                          borderRadius: 10, padding: 20, textAlign: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: 40, marginBottom: 8 }}>🎉</div>
              <p style={{ fontFamily: 'Cinzel, serif', color: '#C9A84C', fontSize: 18, marginBottom: 4 }}>
                Winner!
              </p>
              <p style={{ color: '#EDE8DC', fontSize: 20, fontWeight: 700, marginBottom: 4 }}>
                {lastResult.winnerName}
              </p>
              <p style={{ fontFamily: 'Cinzel, serif', fontSize: 16, color: '#E2C97E', letterSpacing: 3, marginBottom: 4 }}>
                {lastResult.winnerTicket}
              </p>
              <p style={{ color: '#EDE8DC80', fontSize: 12 }}>Prize: {lastResult.prizeName}</p>
              {lastResult.prizeValue && (
                <p style={{ color: '#C9A84C', fontSize: 14, fontWeight: 700 }}>{lastResult.prizeValue}</p>
              )}
            </div>
          )}

          <button onClick={() => executeDraw(false)} disabled={drawing || !selectedPrize}
                  style={{ ...S.btn, ...S.gold, width: '100%', padding: 14, fontSize: 15, marginBottom: 8,
                            opacity: (drawing || !selectedPrize) ? 0.5 : 1 }}>
            {drawing ? '🎲 Drawing...' : '⚡ Execute Draw'}
          </button>
          {selectedPrize && prizes.find(p => p.id === selectedPrize)?.won && (
            <button onClick={() => executeDraw(true)} disabled={drawing}
                    style={{ ...S.btn, ...S.ghost, width: '100%' }}>
              🔁 Re-draw (Override Previous Winner)
            </button>
          )}
        </div>

        {/* Prize status */}
        <div style={S.card}>
          <h4 style={{ fontFamily: 'Cinzel, serif', color: '#C9A84C', marginBottom: 16, fontSize: 16 }}>
            🏆 Prize Status
          </h4>
          {available.length === 0 && (
            <p style={{ color: '#EDE8DC60', fontSize: 13 }}>No prizes added yet. Add prizes in the Prizes tab.</p>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {available.map(p => {
              const ts2 = TIER_STYLE[p.tier] || TIER_STYLE.CONSOLATION;
              return (
                <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 12,
                                          padding: '10px 14px', borderRadius: 8,
                                          background: p.won ? 'rgba(201,168,76,0.08)' : '#0A1410',
                                          border: `1px solid ${p.won ? 'rgba(201,168,76,0.3)' : '#1C2E22'}` }}>
                  <span>{ts2.icon}</span>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: 13, color: '#EDE8DC', fontWeight: 600 }}>{p.name}</p>
                    {p.won && <p style={{ fontSize: 11, color: '#C9A84C' }}>Won by {p.winnerName} · {p.winnerTicket}</p>}
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
                                 background: p.won ? 'rgba(201,168,76,0.15)' : 'rgba(52,211,153,0.1)',
                                 color: p.won ? '#C9A84C' : '#34D399', border: `1px solid ${p.won ? 'rgba(201,168,76,0.3)' : 'rgba(52,211,153,0.3)'}` }}>
                    {p.won ? 'DRAWN' : 'OPEN'}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function ManagePrizes({ prizes, onAdd, onEdit, onDelete }: {
  prizes: RafflePrize[];
  onAdd: (data: any) => void;
  onEdit: (id: string, data: any) => void;
  onDelete: (id: string) => void;
}) {
  const blank = { name: '', tier: 'CONSOLATION', description: '', value: '', imageUrl: '', sortOrder: 1 };
  const [form, setForm] = useState(blank);
  const [editing, setEditing] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  const submit = () => {
    if (!form.name.trim()) return;
    if (editing) { onEdit(editing, form); setEditing(null); }
    else { onAdd(form); }
    setForm(blank); setShowForm(false);
  };

  const startEdit = (p: RafflePrize) => {
    setForm({ name: p.name, tier: p.tier, description: p.description, value: p.value,
              imageUrl: p.imageUrl, sortOrder: p.sortOrder });
    setEditing(p.id); setShowForm(true);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h4 style={{ fontFamily: 'Cinzel, serif', color: '#EDE8DC', fontSize: 16 }}>
          Prizes ({prizes.length})
        </h4>
        <button onClick={() => { setEditing(null); setForm(blank); setShowForm(!showForm); }}
                style={{ ...S.btn, ...S.gold, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Plus size={14} /> Add Prize
        </button>
      </div>

      {showForm && (
        <div style={{ ...S.card, marginBottom: 20, border: '1px solid rgba(201,168,76,0.3)' }}>
          <h5 style={{ color: '#C9A84C', marginBottom: 16, fontFamily: 'Cinzel, serif' }}>
            {editing ? 'Edit Prize' : 'New Prize'}
          </h5>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: 12 }}>
            {[
              { key: 'name', label: 'Prize Name', placeholder: 'e.g. Grand Prize' },
              { key: 'value', label: 'Prize Value', placeholder: 'e.g. ₦500,000 or Toyota Corolla' },
              { key: 'imageUrl', label: 'Image URL (Cloudinary)', placeholder: 'https://res.cloudinary.com/...' },
            ].map(f => (
              <div key={f.key}>
                <label style={S.label}>{f.label}</label>
                <input style={S.input} placeholder={f.placeholder}
                       value={(form as any)[f.key]}
                       onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))} />
              </div>
            ))}
            <div>
              <label style={S.label}>Prize Tier</label>
              <select style={{ ...S.input, cursor: 'pointer' }}
                      value={form.tier} onChange={e => setForm(prev => ({ ...prev, tier: e.target.value }))}>
                {Object.entries(TIER_STYLE).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={S.label}>Sort Order</label>
              <input style={S.input} type='number' min={1}
                     value={form.sortOrder}
                     onChange={e => setForm(prev => ({ ...prev, sortOrder: parseInt(e.target.value) || 1 }))} />
            </div>
          </div>
          <div style={{ marginTop: 12 }}>
            <label style={S.label}>Description</label>
            <textarea style={{ ...S.input, height: 80, resize: 'vertical' }}
                      placeholder='Describe this prize...'
                      value={form.description}
                      onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))} />
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <button onClick={submit} style={{ ...S.btn, ...S.gold }}>
              {editing ? 'Save Changes' : 'Add Prize'}
            </button>
            <button onClick={() => { setShowForm(false); setEditing(null); }}
                    style={{ ...S.btn, ...S.ghost }}>Cancel</button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {prizes.length === 0 && (
          <div style={{ ...S.card, textAlign: 'center', padding: 40 }}>
            <Gift size={40} style={{ color: '#EDE8DC40', marginBottom: 12 }} />
            <p style={{ color: '#EDE8DC60' }}>No prizes yet. Add prizes to get started.</p>
          </div>
        )}
        {prizes.map(p => {
          const ts2 = TIER_STYLE[p.tier] || TIER_STYLE.CONSOLATION;
          return (
            <div key={p.id} style={{ ...S.card, display: 'flex', alignItems: 'center', gap: 14 }}>
              {p.imageUrl && (
                <img src={p.imageUrl} alt={p.name}
                     style={{ width: 60, height: 60, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }}
                     onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
              )}
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  {ts2.icon}
                  <span style={{ fontSize: 10, color: '#EDE8DC60', letterSpacing: 2, textTransform: 'uppercase' }}>
                    {ts2.label}
                  </span>
                  {p.won && <span style={{ fontSize: 10, background: 'rgba(201,168,76,0.2)', color: '#C9A84C',
                                           padding: '2px 8px', borderRadius: 20 }}>WON</span>}
                </div>
                <p style={{ fontSize: 14, color: '#EDE8DC', fontWeight: 600 }}>{p.name}</p>
                {p.value && <p style={{ fontSize: 13, color: '#C9A84C' }}>{p.value}</p>}
                {p.won && <p style={{ fontSize: 12, color: '#EDE8DC60' }}>Winner: {p.winnerName} · {p.winnerTicket}</p>}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => startEdit(p)} title='Edit'
                        style={{ ...S.btn, ...S.ghost, padding: '7px 12px' }}>
                  <Edit2 size={13} />
                </button>
                <button onClick={() => { if (window.confirm('Delete this prize?')) onDelete(p.id); }}
                        title='Delete' style={{ ...S.btn, ...S.danger, padding: '7px 12px' }}>
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TicketsList({ tickets }: { tickets: RaffleTicket[] }) {
  const [search, setSearch] = useState('');
  const filtered = tickets.filter(t =>
    !search || t.holderName.toLowerCase().includes(search.toLowerCase()) ||
    t.holderPhone.includes(search) || t.ticketNumber.includes(search.toUpperCase()) ||
    (t.holderEmail && t.holderEmail.toLowerCase().includes(search.toLowerCase()))
  );
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h4 style={{ fontFamily: 'Cinzel, serif', color: '#EDE8DC', fontSize: 16 }}>
          All Tickets ({tickets.length})
        </h4>
        <div style={{ position: 'relative' }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#EDE8DC60' }} />
          <input style={{ ...S.input, width: 240, paddingLeft: 32 }} placeholder='Search name, phone, ticket...'
                 value={search} onChange={e => setSearch(e.target.value)} />
        </div>
      </div>
      {filtered.length === 0 && (
        <div style={{ ...S.card, textAlign: 'center', padding: 40 }}>
          <Ticket size={40} style={{ color: '#EDE8DC40', marginBottom: 12 }} />
          <p style={{ color: '#EDE8DC60' }}>{search ? 'No tickets match your search' : 'No tickets sold yet'}</p>
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {filtered.slice(0, 50).map(t => (
          <div key={t.id} style={{ ...S.card, display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px' }}>
            <div style={{ minWidth: 130 }}>
              <p style={{ fontFamily: 'Cinzel, serif', fontSize: 14, color: '#C9A84C', letterSpacing: 2 }}>
                {t.ticketNumber}
              </p>
              <p style={{ fontSize: 10, color: t.drawnForPrize ? '#F59E0B' : '#34D399',
                           marginTop: 2, letterSpacing: 1 }}>
                {t.drawnForPrize ? '🏆 WINNER' : '✅ ACTIVE'}
              </p>
            </div>
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: 13, color: '#EDE8DC', fontWeight: 600 }}>{t.holderName}</p>
              <p style={{ fontSize: 12, color: '#EDE8DC80' }}>{t.holderPhone} · {t.holderEmail}</p>
            </div>
            <p style={{ fontSize: 11, color: '#EDE8DC60' }}>
              {new Date(t.purchasedAt).toLocaleDateString('en-NG')}
            </p>
          </div>
        ))}
        {filtered.length > 50 && (
          <p style={{ textAlign: 'center', color: '#EDE8DC60', fontSize: 12 }}>
            Showing 50 of {filtered.length} tickets
          </p>
        )}
      </div>
    </div>
  );
}

function ResultsList({ results, onDelete, isAdmin }: {
  results: RaffleResult[]; onDelete?: (id: string) => void; isAdmin: boolean;
}) {
  return (
    <div>
      <h4 style={{ fontFamily: 'Cinzel, serif', color: '#EDE8DC', fontSize: 16, marginBottom: 16 }}>
        Draw Results ({results.length})
      </h4>
      {results.length === 0 && (
        <div style={{ ...S.card, textAlign: 'center', padding: 40 }}>
          <Trophy size={40} style={{ color: '#EDE8DC40', marginBottom: 12 }} />
          <p style={{ color: '#EDE8DC60' }}>No draws have been conducted yet.</p>
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {results.map(r => {
          const ts2 = TIER_STYLE[r.prizeTier] || TIER_STYLE.CONSOLATION;
          return (
            <div key={r.id} style={{ ...S.card, border: `1px solid ${ts2.border}`, position: 'relative' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                  {ts2.icon}
                  <div>
                    <p style={{ fontSize: 11, color: '#EDE8DC60', letterSpacing: 2, textTransform: 'uppercase' }}>
                      {ts2.label}
                    </p>
                    <p style={{ fontFamily: 'Cinzel, serif', fontSize: 16, color: '#EDE8DC' }}>{r.prizeName}</p>
                    {r.prizeValue && <p style={{ color: '#C9A84C', fontSize: 14, fontWeight: 700 }}>{r.prizeValue}</p>}
                  </div>
                </div>
                {isAdmin && onDelete && (
                  <button onClick={() => { if (window.confirm('Remove this result? This will un-mark the prize and ticket.')) onDelete(r.id); }}
                          style={{ ...S.btn, ...S.danger, padding: '6px 10px' }}>
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
              <div style={{ background: '#0A1410', borderRadius: 8, padding: '14px 16px' }}>
                <p style={{ fontSize: 11, color: '#EDE8DC60', marginBottom: 8, letterSpacing: 2 }}>WINNER</p>
                <p style={{ fontSize: 20, fontWeight: 700, color: '#EDE8DC', marginBottom: 4 }}>{r.winnerName}</p>
                <p style={{ fontFamily: 'Cinzel, serif', fontSize: 18, color: '#C9A84C',
                             letterSpacing: 3, marginBottom: 4 }}>{r.winnerTicket}</p>
                <p style={{ fontSize: 12, color: '#EDE8DC60' }}>
                  Drawn: {new Date(r.drawnAt).toLocaleString('en-NG')}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MyTicketsLookup({ config }: { config: RaffleConfig }) {
  const [search, setSearch] = useState('');
  const [searchType, setSearchType] = useState<'phone'|'email'>('phone');
  const [tickets, setTickets] = useState<RaffleTicket[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const lookup = async () => {
    if (!search.trim()) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ [searchType]: search.trim() });
      const r = await fetch(`/api/raffle/my-tickets?${params}`, { headers: re_authHeaders() });
      const d = await r.json();
      setTickets(d.tickets || []);
      setSearched(true);
    } catch { setTickets([]); setSearched(true); }
    setLoading(false);
  };

  return (
    <div style={{ maxWidth: 560, margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <p style={S.eyebrow}>Ticket Lookup</p>
        <h3 style={{ ...S.h2, fontSize: 'clamp(1.2rem,2.5vw,1.6rem)' }}>Find My Tickets</h3>
        <p style={{ color: '#EDE8DC80', fontSize: 14 }}>
          Enter your phone number or email to retrieve your ticket numbers.
        </p>
      </div>
      <div style={S.card}>
        <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
          {(['phone','email'] as const).map(t => (
            <button key={t} onClick={() => setSearchType(t)}
                    style={{ ...S.btn, flex: 1,
                              ...(searchType === t ? S.gold : S.ghost) }}>
              {t === 'phone' ? '📞 Phone' : '✉️ Email'}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <input style={{ ...S.input, flex: 1 }}
                 type={searchType === 'email' ? 'email' : 'tel'}
                 placeholder={searchType === 'phone' ? '08012345678' : 'you@example.com'}
                 value={search} onChange={e => setSearch(e.target.value)}
                 onKeyDown={e => e.key === 'Enter' && lookup()} />
          <button onClick={lookup} disabled={loading}
                  style={{ ...S.btn, ...S.gold, minWidth: 100, opacity: loading ? 0.6 : 1 }}>
            {loading ? '...' : 'Search'}
          </button>
        </div>
        {searched && (
          <div style={{ marginTop: 20 }}>
            {tickets.length === 0 ? (
              <p style={{ color: '#EDE8DC60', fontSize: 13, textAlign: 'center' }}>
                No tickets found for that {searchType}.
              </p>
            ) : (
              <>
                <p style={{ color: '#34D399', fontSize: 13, marginBottom: 12 }}>
                  ✅ Found {tickets.length} ticket{tickets.length > 1 ? 's' : ''}
                </p>
                {tickets.map(t => (
                  <div key={t.id} style={{ ...S.card, marginBottom: 10, display: 'flex',
                                           justifyContent: 'space-between', alignItems: 'center',
                                           border: t.drawnForPrize ? '1px solid rgba(201,168,76,0.4)' : '1px solid #1C2E22' }}>
                    <div>
                      <p style={{ fontFamily: 'Cinzel, serif', fontSize: 18, color: '#C9A84C', letterSpacing: 3 }}>
                        {t.ticketNumber}
                      </p>
                      <p style={{ fontSize: 12, color: t.drawnForPrize ? '#F59E0B' : '#34D399', marginTop: 2 }}>
                        {t.drawnForPrize ? '🏆 Winner!' : '✅ Active'}
                      </p>
                    </div>
                    <button onClick={() => printTicket(t, config)}
                            style={{ ...S.btn, ...S.ghost, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Printer size={13} /> Print
                    </button>
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── MAIN COMPONENT ───────────────────────────────────────────────
interface RaffleEngineProps {
  currentUser?: { email: string; name?: string; systemRole?: string } | null;
}

const ADMIN_ROLES = ['PLATFORM_ADMIN', 'RAFFLE_MANAGER', 'ACCOUNTANT', 'FESTIVAL_COORDINATOR'];

const RaffleEngine: React.FC<RaffleEngineProps> = ({ currentUser }) => {
  const isAdmin = !!(currentUser && ADMIN_ROLES.includes(currentUser.systemRole || ''));

  const [prizes, setPrizes]     = useState<RafflePrize[]>([]);
  const [tickets, setTickets]   = useState<RaffleTicket[]>([]);
  const [results, setResults]   = useState<RaffleResult[]>([]);
  const [config, setConfig]     = useState<RaffleConfig>({ ticketPrice: 1000, currency: 'NGN', isOpen: true });
  const [summary, setSummary]   = useState<any>(null);
  const [loading, setLoading]   = useState(true);
  const [showBuy, setShowBuy]   = useState(false);
  const [paystackKey, setPsKey] = useState('');
  const [activeTab, setActiveTab] = useState<'prizes'|'buy'|'mytickets'|'results'|'draw'|'manage'|'alltickets'>('prizes');
  const [drawErr, setDrawErr]   = useState('');
  const [drawMsg, setDrawMsg]   = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [prRes, rsRes, cfRes] = await Promise.all([
        fetch('/api/public/raffle-prizes').then(r => r.json()),
        fetch('/api/public/raffle-results').then(r => r.json()),
        fetch('/api/raffle/config').then(r => r.json()),
      ]);
      setPrizes(prRes.prizes || []);
      setResults(rsRes.results || []);
      setConfig(cfRes.config || { ticketPrice: 1000, currency: 'NGN', isOpen: true });
      setPsKey(cfRes.paystackPublicKey || '');
      if (isAdmin) {
        const [tkRaw, smRaw] = await Promise.all([
          fetch('/api/raffle/admin/tickets', { headers: re_authHeaders() }),
          fetch('/api/raffle/admin/summary',  { headers: re_authHeaders() }),
        ]);
        const tkRes = await tkRaw.json();
        const smRes = await smRaw.json();
        setTickets(tkRes.tickets || []);
        setSummary(smRes.summary || null);
      }
    } catch { /* silent */ }
    setLoading(false);
  }, [isAdmin]);

  useEffect(() => { load(); }, [load]);

  // URL param: ?tab=raffle&verify=REFERENCE
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('verify');
    if (ref) {
      fetch('/api/raffle/verify-payment', {
        method: 'POST', headers: re_authHeaders(),
        body: JSON.stringify({ reference: ref })
      }).then(r => r.json()).then(d => {
        if (d.success) { setActiveTab('mytickets'); load(); }
      }).catch(() => {});
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [load]);

  const handleDraw = async (prizeId: string, redraw = false): Promise<RaffleResult | null> => {
    setDrawErr(''); setDrawMsg('');
    try {
      const r = await fetch('/api/raffle/admin/draw', {
        method: 'POST',
        headers: re_authHeaders(),
        body: JSON.stringify({ prizeId, redraw })
      });
      const d = await r.json();
      if (d.success) { setDrawMsg(`Winner drawn! ${d.winner.winnerName} - ${d.winner.winnerTicket}`); await load(); return d.winner; }
      else { setDrawErr(d.error || 'Draw failed'); return null; }
    } catch { setDrawErr('Network error during draw'); return null; }
  };

  const handleAddPrize = async (data: any) => {
    await fetch('/api/raffle/admin/prizes', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${re_getToken() || ''}` },
      body: JSON.stringify(data)
    });
    load();
  };

  const handleEditPrize = async (id: string, data: any) => {
    await fetch(`/api/raffle/admin/prizes/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${re_getToken() || ''}` },
      body: JSON.stringify(data)
    });
    load();
  };

  const handleDeletePrize = async (id: string) => {
    await fetch(`/api/raffle/admin/prizes/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${re_getToken() || ''}` }
    });
    load();
  };

  const handleDeleteResult = async (id: string) => {
    await fetch(`/api/raffle/admin/results/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${re_getToken() || ''}` }
    });
    load();
  };

  // Tab definitions
  const publicTabs = [
    { id: 'prizes',    label: '🎁 Prizes' },
    { id: 'mytickets', label: '🔍 My Tickets' },
    { id: 'results',   label: '🏆 Results' },
  ];
  const adminTabs = [
    { id: 'draw',      label: '🎛️ Draw Panel' },
    { id: 'manage',    label: '🎁 Manage Prizes' },
    { id: 'alltickets',label: '📋 All Tickets' },
    { id: 'results',   label: '📊 Results' },
  ];
  const tabs = isAdmin ? adminTabs : publicTabs;

  return (
    <div style={S.page}>
      {/* Hero */}
      <div style={{ background: 'linear-gradient(180deg,#0E1812 0%,#070D0A 100%)',
                    borderBottom: '1px solid #1C2E22', padding: 'clamp(32px,6vw,60px) 24px',
                    textAlign: 'center' }}>
        <p style={S.eyebrow}>ERUE Festival 2026</p>
        <h1 style={{ fontFamily: 'Cinzel, serif', fontSize: 'clamp(1.6rem,4vw,2.8rem)',
                     color: '#EDE8DC', marginBottom: 8 }}>
          Grand Raffle Draw
        </h1>
        <p style={{ color: '#EDE8DC80', fontSize: 14, maxWidth: 520, margin: '0 auto 24px',
                    lineHeight: 1.7 }}>
          Win amazing prizes at the ERUE Festival 2026 Grand Finale.<br />
          Wednesday, 30 December 2026 · Palace of the Ogieneni, Jattu-Uzairue
        </p>
        {!isAdmin && config.isOpen && (
          <button onClick={() => setShowBuy(true)}
                  style={{ ...S.btn, ...S.gold, fontSize: 15, padding: '13px 32px' }}>
            🎟️ Buy Tickets — {fmt(config.ticketPrice)} each
          </button>
        )}
        {!config.isOpen && !isAdmin && (
          <div style={{ display: 'inline-block', background: 'rgba(239,68,68,0.1)',
                        border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8,
                        padding: '10px 24px', color: '#F87171', fontSize: 13 }}>
            🔒 Ticket sales are currently closed
          </div>
        )}
      </div>

      {/* Tabs */}
      <div style={{ borderBottom: '1px solid #1C2E22', padding: '0 24px',
                    display: 'flex', gap: 4, overflowX: 'auto' }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id as any)}
                  style={{ ...S.btn, background: 'none', border: 'none', padding: '12px 16px',
                            color: activeTab === t.id ? '#C9A84C' : '#EDE8DC80',
                            borderBottom: activeTab === t.id ? '2px solid #C9A84C' : '2px solid transparent',
                            borderRadius: 0, whiteSpace: 'nowrap', fontSize: 13 }}>
            {t.label}
          </button>
        ))}
        {!isAdmin && config.isOpen && (
          <button onClick={() => setShowBuy(true)}
                  style={{ ...S.btn, ...S.gold, marginLeft: 'auto', padding: '8px 20px',
                            alignSelf: 'center', fontSize: 12 }}>
            🎟️ Buy Now
          </button>
        )}
        <button onClick={load} title='Refresh'
                style={{ ...S.btn, ...S.ghost, padding: '8px 12px', alignSelf: 'center',
                          marginLeft: isAdmin ? 'auto' : 8 }}>
          <RefreshCw size={13} />
        </button>
      </div>

      {/* Messages */}
      {(drawErr || drawMsg) && (
        <div style={{ margin: '16px 24px 0', padding: '12px 16px', borderRadius: 8,
                      background: drawErr ? 'rgba(239,68,68,0.1)' : 'rgba(52,211,153,0.1)',
                      border: `1px solid ${drawErr ? 'rgba(239,68,68,0.3)' : 'rgba(52,211,153,0.3)'}`,
                      color: drawErr ? '#F87171' : '#34D399', fontSize: 13,
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{drawErr || drawMsg}</span>
          <button onClick={() => { setDrawErr(''); setDrawMsg(''); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}>✕</button>
        </div>
      )}

      {/* Content */}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '32px 24px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 60 }}>
            <RefreshCw size={32} style={{ color: '#C9A84C', animation: 'spin 1.5s linear infinite' }} />
            <p style={{ color: '#EDE8DC60', marginTop: 12 }}>Loading raffle data...</p>
          </div>
        ) : (
          <>
            {/* PUBLIC TABS */}
            {!isAdmin && activeTab === 'prizes' && (
              <div>
                <div style={{ textAlign: 'center', marginBottom: 32 }}>
                  <p style={S.eyebrow}>Win Big</p>
                  <h2 style={S.h2}>Festival Prizes</h2>
                  <p style={{ color: '#EDE8DC80', fontSize: 14 }}>
                    {prizes.filter(p => !p.won).length} prize{prizes.filter(p => !p.won).length !== 1 ? 's' : ''} remaining to be won
                  </p>
                </div>
                {prizes.length === 0 ? (
                  <div style={{ ...S.card, textAlign: 'center', padding: 60 }}>
                    <Gift size={48} style={{ color: '#EDE8DC40', marginBottom: 16 }} />
                    <p style={{ color: '#EDE8DC60', fontSize: 15 }}>Prizes will be announced soon. Stay tuned!</p>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))', gap: 16 }}>
                    {prizes.map(p => (
                      <PrizeCard key={p.id} prize={p} onBuy={() => setShowBuy(true)} />
                    ))}
                  </div>
                )}
              </div>
            )}
            {!isAdmin && activeTab === 'mytickets' && <MyTicketsLookup config={config} />}
            {!isAdmin && activeTab === 'results' && <ResultsList results={results} isAdmin={false} />}

            {/* ADMIN TABS */}
            {isAdmin && activeTab === 'draw' && (
              <DrawPanel prizes={prizes} onDraw={handleDraw} summary={summary} />
            )}
            {isAdmin && activeTab === 'manage' && (
              <ManagePrizes prizes={prizes} onAdd={handleAddPrize} onEdit={handleEditPrize} onDelete={handleDeletePrize} />
            )}
            {isAdmin && activeTab === 'alltickets' && <TicketsList tickets={tickets} />}
            {isAdmin && activeTab === 'results' && (
              <ResultsList results={results} isAdmin onDelete={handleDeleteResult} />
            )}
          </>
        )}
      </div>

      {/* Buy ticket modal */}
      {showBuy && <BuyTicketModal config={config} paystackKey={paystackKey} onClose={() => { setShowBuy(false); load(); }} />}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.5; } }
      `}</style>
    </div>
  );
};

export default RaffleEngine;
